import {memo, useEffect, useMemo, useRef, useState} from "react";
import markdownStyle from "./blogMarkdown.module.less";

const MERMAID_NODE_STROKE = 'rgba(228, 238, 248, 0.4)';
const MERMAID_CLUSTER_STROKE = 'rgba(228, 238, 248, 0.24)';
const MERMAID_EDGE_STROKE = '#00cec9';
const MERMAID_ARROW_STROKE = '#fd79a8';
const MERMAID_TEXT_COLOR = 'rgba(232, 240, 248, 0.96)';
const MERMAID_ARROW_DEFAULT = 'rgba(192, 202, 214, 0.82)';

let hasInitMermaid = false;
let mermaidIdSeed = 0;

const createMermaidId = () => {
    mermaidIdSeed += 1;
    return `blog-mermaid-${mermaidIdSeed}`;
};

const MERMAID_THEME_CSS = `
svg {
  overflow: visible;
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
}
.node rect,
.node circle,
.node ellipse,
.node polygon,
.node path,
.actor,
.labelBox,
.label-container,
.label-container rect,
.label-container polygon,
.label-container path {
  fill: transparent !important;
  stroke: ${MERMAID_NODE_STROKE} !important;
  stroke-width: 1px !important;
  filter: none !important;
  cursor: pointer;
  pointer-events: all !important;
  transition: stroke 0.18s ease, filter 0.18s ease, opacity 0.18s ease;
}
.node:hover rect,
.node:hover circle,
.node:hover ellipse,
.node:hover polygon,
.node:hover path,
.node:hover .actor,
.node:hover .labelBox,
.node:hover .label-container,
.node:hover .label-container rect,
.node:hover .label-container polygon,
.node:hover .label-container path {
  stroke: var(--blog-mermaid-line, ${MERMAID_EDGE_STROKE}) !important;
  filter: drop-shadow(0 0 6px color-mix(in srgb, var(--blog-mermaid-line, ${MERMAID_EDGE_STROKE}) 35%, transparent));
}
.node rect {
  rx: 2px !important;
  ry: 2px !important;
}
.cluster rect,
.cluster polygon {
  fill: transparent !important;
  stroke: ${MERMAID_CLUSTER_STROKE} !important;
  stroke-width: 1px !important;
  stroke-dasharray: 5 5 !important;
}
.edgePaths path,
.edgePath .path,
.flowchart-link,
.relationshipLine,
.transition,
.messageLine0,
.messageLine1,
.loopLine,
.sectionTitle,
.actor-line,
.signalLine {
  fill: none !important;
  stroke: rgba(192, 202, 214, 0.42) !important;
  stroke-width: 2.2px !important;
  stroke-dasharray: 14 10 !important;
  stroke-dashoffset: 0 !important;
  stroke-linecap: round !important;
  stroke-linejoin: round !important;
  animation: none !important;
  filter: none !important;
  opacity: 1 !important;
  pointer-events: none !important;
  vector-effect: non-scaling-stroke !important;
}
.edgePaths path.blog-mermaid-edge-linked,
.edgePath .path.blog-mermaid-edge-linked,
.flowchart-link.blog-mermaid-edge-linked,
.relationshipLine.blog-mermaid-edge-linked,
.transition.blog-mermaid-edge-linked,
.messageLine0.blog-mermaid-edge-linked,
.messageLine1.blog-mermaid-edge-linked,
.loopLine.blog-mermaid-edge-linked,
.sectionTitle.blog-mermaid-edge-linked,
.actor-line.blog-mermaid-edge-linked,
.signalLine.blog-mermaid-edge-linked {
  stroke: var(--blog-mermaid-line, ${MERMAID_EDGE_STROKE}) !important;
  stroke-width: 2.8px !important;
  stroke-dasharray: 14 10 !important;
  stroke-dashoffset: 0 !important;
  animation: mermaidLineDash 1.2s linear infinite, mermaidLinePulse 1.8s ease-in-out infinite !important;
  filter: drop-shadow(0 0 4px color-mix(in srgb, var(--blog-mermaid-line, ${MERMAID_EDGE_STROKE}) 40%, transparent)) !important;
  opacity: 1 !important;
}
.edgeLabel rect,
.labelBkg {
  fill: transparent !important;
  stroke: rgba(228, 238, 248, 0.28) !important;
  stroke-width: 1px !important;
  rx: 2px !important;
  ry: 2px !important;
  pointer-events: none !important;
}
.label,
.label text,
.label tspan,
.nodeLabel,
.edgeLabel,
.cluster-label,
text,
tspan {
  fill: ${MERMAID_TEXT_COLOR} !important;
  color: ${MERMAID_TEXT_COLOR} !important;
  font-weight: 600 !important;
}
.node .label,
.node .label *,
.node foreignObject,
.node foreignObject *,
.node .nodeLabel,
.node .nodeLabel * {
  pointer-events: none !important;
}
.edgeLabel,
.edgeLabel *,
.edgeLabels .label,
.edgeLabels .label * {
  pointer-events: none !important;
}
foreignObject div,
foreignObject span,
foreignObject p {
  color: ${MERMAID_TEXT_COLOR} !important;
  fill: ${MERMAID_TEXT_COLOR} !important;
  background: transparent !important;
  line-height: 1.45 !important;
  font-weight: 600 !important;
}
.edgeLabel.blog-mermaid-edge-label-linked .labelBkg,
.edgeLabel.blog-mermaid-edge-label-linked rect,
.edgeLabels .label.blog-mermaid-edge-label-linked .labelBkg,
.edgeLabels .label.blog-mermaid-edge-label-linked rect,
.edgeLabels .label.blog-mermaid-edge-label-linked rect {
  stroke: var(--blog-mermaid-arrow, ${MERMAID_ARROW_STROKE}) !important;
  filter: drop-shadow(0 0 4px color-mix(in srgb, var(--blog-mermaid-arrow, ${MERMAID_ARROW_STROKE}) 32%, transparent)) !important;
}
.edgeLabel.blog-mermaid-edge-label-linked text,
.edgeLabel.blog-mermaid-edge-label-linked tspan,
.edgeLabel.blog-mermaid-edge-label-linked .label,
.edgeLabel.blog-mermaid-edge-label-linked .label *,
.edgeLabels .label.blog-mermaid-edge-label-linked text,
.edgeLabels .label.blog-mermaid-edge-label-linked tspan,
.edgeLabels .label.blog-mermaid-edge-label-linked .label,
.edgeLabels .label.blog-mermaid-edge-label-linked .label * {
  fill: rgba(245, 249, 255, 0.98) !important;
}
.blog-mermaid-edge-arrow {
  fill: ${MERMAID_ARROW_DEFAULT} !important;
  stroke: ${MERMAID_ARROW_DEFAULT} !important;
  stroke-width: 1px !important;
  pointer-events: none !important;
}
.blog-mermaid-edge-arrow.blog-mermaid-edge-arrow-linked {
  fill: var(--blog-mermaid-line, ${MERMAID_EDGE_STROKE}) !important;
  stroke: var(--blog-mermaid-line, ${MERMAID_EDGE_STROKE}) !important;
  filter: drop-shadow(0 0 4px color-mix(in srgb, var(--blog-mermaid-line, ${MERMAID_EDGE_STROKE}) 40%, transparent)) !important;
}
@keyframes mermaidLineDash {
  to {
    stroke-dashoffset: -26;
  }
}
@keyframes mermaidLinePulse {
  0%,
  100% {
    opacity: 0.48;
    filter: drop-shadow(0 0 3px color-mix(in srgb, var(--blog-mermaid-line, ${MERMAID_EDGE_STROKE}) 18%, transparent));
  }
  50% {
    opacity: 0.72;
    filter: drop-shadow(0 0 6px color-mix(in srgb, var(--blog-mermaid-line, ${MERMAID_EDGE_STROKE}) 28%, transparent));
  }
}
`;

const injectMermaidSvgTheme = (svg = '') => {
    if (typeof svg !== 'string' || svg.trim() === '') {
        return svg;
    }
    const styleBlock = `
<style>
${MERMAID_THEME_CSS}
</style>`;
    return svg.replace(/(<svg\b[^>]*>)/i, `$1${styleBlock}`);
};

const extractMermaidNodeKey = (rawId = '') => {
    const normalized = String(rawId || '').trim();
    if (!normalized) {
        return '';
    }
    const flowchartMatch = normalized.match(/^flowchart-([^-]+)-\d+$/i);
    if (flowchartMatch) {
        return flowchartMatch[1];
    }
    const genericMatch = normalized.match(/^([A-Za-z0-9_]+)(?:-\d+)?$/);
    return genericMatch ? genericMatch[1] : '';
};

const extractMermaidEdgeKeys = (rawId = '') => {
    const normalized = String(rawId || '').trim();
    if (!normalized) {
        return {sourceKey: '', targetKey: ''};
    }
    const match = normalized.match(/^L_(.+?)_(.+?)_\d+$/);
    if (!match) {
        return {sourceKey: '', targetKey: ''};
    }
    return {sourceKey: match[1], targetKey: match[2]};
};

const normalizeMermaidSvgAppearance = (svg = '') => {
    return svg;
};

const setMermaidMarkerEnd = (element, markerRef) => {
    if (!element || !markerRef) {
        return;
    }
    element.setAttribute('marker-end', markerRef);
    if (element.style?.setProperty) {
        element.style.setProperty('marker-end', markerRef);
    }
};

const createEdgeArrowPath = (edgeElement) => {
    if (!edgeElement || typeof edgeElement.getTotalLength !== 'function' || typeof edgeElement.getPointAtLength !== 'function') {
        return '';
    }
    const totalLength = edgeElement.getTotalLength();
    if (!Number.isFinite(totalLength) || totalLength <= 0) {
        return '';
    }
    const tip = edgeElement.getPointAtLength(totalLength);
    const previous = edgeElement.getPointAtLength(Math.max(totalLength - 12, 0));
    const dx = tip.x - previous.x;
    const dy = tip.y - previous.y;
    const vectorLength = Math.hypot(dx, dy);
    if (!Number.isFinite(vectorLength) || vectorLength === 0) {
        return '';
    }
    const ux = dx / vectorLength;
    const uy = dy / vectorLength;
    const arrowLength = 9;
    const arrowWidth = 5.2;
    const tipX = tip.x - ux * 1.5;
    const tipY = tip.y - uy * 1.5;
    const backX = tipX - ux * arrowLength;
    const backY = tipY - uy * arrowLength;
    const leftX = backX + (-uy * arrowWidth);
    const leftY = backY + (ux * arrowWidth);
    const rightX = backX - (-uy * arrowWidth);
    const rightY = backY - (ux * arrowWidth);
    return `M ${tipX} ${tipY} L ${leftX} ${leftY} L ${rightX} ${rightY} Z`;
};

const MermaidBlock = memo(({code, onCopy, onCopyEnter, onCopyLeave}) => {
    const [renderedSvg, setRenderedSvg] = useState('');
    const [renderError, setRenderError] = useState('');
    const renderId = useMemo(() => createMermaidId(), []);
    const canvasRef = useRef(null);

    useEffect(() => {
        const source = String(code || '').trim();
        if (!source) {
            setRenderedSvg('');
            setRenderError('');
            return;
        }
        let cancelled = false;
        const renderMermaid = async () => {
            try {
                const mermaidModule = await import('mermaid');
                const mermaid = mermaidModule.default || mermaidModule;
                if (!hasInitMermaid) {
                    mermaid.initialize({
                        startOnLoad: false,
                        securityLevel: 'strict',
                        theme: 'base',
                        themeVariables: {
                            background: 'transparent',
                            mainBkg: '#12161d',
                            secondBkg: '#10161e',
                            tertiaryColor: '#0c1017',
                            primaryColor: '#12161d',
                            nodeTextColor: '#e8f0f8',
                            primaryTextColor: '#e8f0f8',
                            secondaryColor: '#10161e',
                            secondaryTextColor: '#e8f0f8',
                            tertiaryTextColor: '#e8f0f8',
                            lineColor: '#00cec9',
                            arrowheadColor: '#c0cad6',
                            textColor: '#e8f0f8',
                            primaryBorderColor: '#d6e3f0',
                            secondaryBorderColor: '#d6e3f0',
                            clusterBkg: 'transparent',
                            clusterBorder: '#d6e3f0',
                            edgeLabelBackground: 'transparent',
                            actorBkg: 'transparent',
                            actorBorder: '#d6e3f0',
                            actorTextColor: '#e8f0f8',
                            signalColor: '#00cec9',
                            signalTextColor: '#e8f0f8',
                            labelBoxBkgColor: 'transparent',
                            labelBoxBorderColor: '#d6e3f0',
                            labelTextColor: '#e8f0f8',
                            loopTextColor: '#e8f0f8',
                            loopLineColor: '#00cec9',
                            noteBorderColor: '#d6e3f0',
                            noteBkgColor: 'transparent',
                            noteTextColor: '#e8f0f8',
                            activationBorderColor: '#d6e3f0',
                            activationBkgColor: 'transparent',
                        },
                        themeCSS: MERMAID_THEME_CSS,
                    });
                    hasInitMermaid = true;
                }
                const {svg} = await mermaid.render(renderId, source);
                if (cancelled) {
                    return;
                }
                setRenderedSvg(injectMermaidSvgTheme(normalizeMermaidSvgAppearance(svg)));
                setRenderError('');
            } catch (error) {
                if (cancelled) {
                    return;
                }
                setRenderedSvg('');
                setRenderError(String(error?.message || error || 'Mermaid render failed'));
            }
        };
        renderMermaid();
        return () => {
            cancelled = true;
        };
    }, [code, renderId]);

    useEffect(() => {
        if (!renderedSvg || !canvasRef.current) {
            return undefined;
        }
        const host = canvasRef.current;
        const svgRoot = host.querySelector('svg');
        if (!svgRoot) {
            return undefined;
        }
        const svgNs = 'http://www.w3.org/2000/svg';
        let defs = svgRoot.querySelector('defs');
        if (!defs) {
            defs = document.createElementNS(svgNs, 'defs');
            svgRoot.insertBefore(defs, svgRoot.firstChild);
        }
        const markerSuffix = renderId.replace(/[^a-zA-Z0-9_-]/g, '-');
        const ensureMarker = (id, color) => {
            let marker = svgRoot.querySelector(`#${id}`);
            if (!marker) {
                marker = document.createElementNS(svgNs, 'marker');
                marker.setAttribute('id', id);
                marker.setAttribute('viewBox', '0 0 10 10');
                marker.setAttribute('refX', '9');
                marker.setAttribute('refY', '5');
                marker.setAttribute('markerWidth', '10');
                marker.setAttribute('markerHeight', '10');
                marker.setAttribute('markerUnits', 'strokeWidth');
                marker.setAttribute('orient', 'auto');
                const path = document.createElementNS(svgNs, 'path');
                path.setAttribute('d', 'M 0 0 L 10 5 L 0 10 z');
                marker.appendChild(path);
                defs.appendChild(marker);
            }
            const path = marker.querySelector('path');
            if (path) {
                path.setAttribute('fill', color);
                path.setAttribute('stroke', color);
                path.setAttribute('stroke-width', '1');
            }
            return `url(#${id})`;
        };
        const defaultArrowRef = ensureMarker(`blog-mermaid-default-arrow-end-${markerSuffix}`, 'rgba(192, 202, 214, 0.82)');
        const activeArrowRef = ensureMarker(`blog-mermaid-active-arrow-end-${markerSuffix}`, '#00cec9');
        host.querySelectorAll('.node').forEach((nodeGroup) => {
            const nodeKey = extractMermaidNodeKey(nodeGroup.getAttribute('id'));
            if (nodeKey) {
                nodeGroup.setAttribute('data-node-key', nodeKey);
            }
        });
        const edgeElements = Array.from(host.querySelectorAll('.edgePaths path, .edgePath .path, .flowchart-link, .relationshipLine, .transition, .messageLine0, .messageLine1, .loopLine, .sectionTitle, .actor-line, .signalLine'));
        edgeElements.forEach((element) => {
            const {sourceKey, targetKey} = extractMermaidEdgeKeys(element.getAttribute('id') || element.getAttribute('data-id'));
            if (sourceKey) {
                element.setAttribute('data-source-key', sourceKey);
            }
            if (targetKey) {
                element.setAttribute('data-target-key', targetKey);
            }
            setMermaidMarkerEnd(element, defaultArrowRef);
        });
        let arrowLayer = svgRoot.querySelector('.blog-mermaid-arrow-layer');
        if (!arrowLayer) {
            arrowLayer = document.createElementNS(svgNs, 'g');
            arrowLayer.setAttribute('class', 'blog-mermaid-arrow-layer');
            svgRoot.appendChild(arrowLayer);
        } else {
            while (arrowLayer.firstChild) {
                arrowLayer.removeChild(arrowLayer.firstChild);
            }
        }
        edgeElements.forEach((element) => {
            const arrowPath = createEdgeArrowPath(element);
            if (!arrowPath) {
                return;
            }
            const arrowElement = document.createElementNS(svgNs, 'path');
            arrowElement.setAttribute('class', 'blog-mermaid-edge-arrow');
            arrowElement.setAttribute('d', arrowPath);
            arrowElement.setAttribute('fill', MERMAID_ARROW_DEFAULT);
            arrowElement.setAttribute('stroke', MERMAID_ARROW_DEFAULT);
            arrowElement.setAttribute('stroke-width', '1');
            arrowElement.setAttribute('pointer-events', 'none');
            arrowElement.setAttribute('vector-effect', 'non-scaling-stroke');
            const sourceKey = element.getAttribute('data-source-key');
            const targetKey = element.getAttribute('data-target-key');
            if (sourceKey) {
                arrowElement.setAttribute('data-source-key', sourceKey);
            }
            if (targetKey) {
                arrowElement.setAttribute('data-target-key', targetKey);
            }
            arrowLayer.appendChild(arrowElement);
        });
        host.querySelectorAll('.edgeLabel .label[data-id], .edgeLabels .label[data-id], .edgeLabel[data-id], .edgeLabels [data-id]').forEach((element) => {
            const {sourceKey, targetKey} = extractMermaidEdgeKeys(element.getAttribute('data-id') || element.getAttribute('id'));
            if (sourceKey) {
                element.setAttribute('data-source-key', sourceKey);
            }
            if (targetKey) {
                element.setAttribute('data-target-key', targetKey);
            }
        });
        const clearLinkedState = () => {
            host.querySelectorAll('.blog-mermaid-edge-linked').forEach((node) => node.classList.remove('blog-mermaid-edge-linked'));
            host.querySelectorAll('.blog-mermaid-edge-label-linked').forEach((node) => node.classList.remove('blog-mermaid-edge-label-linked'));
            host.querySelectorAll('.blog-mermaid-edge-arrow-linked').forEach((node) => node.classList.remove('blog-mermaid-edge-arrow-linked'));
            host.querySelectorAll('.edgePaths path, .edgePath .path, .flowchart-link, .relationshipLine, .transition, .messageLine0, .messageLine1, .loopLine, .sectionTitle, .actor-line, .signalLine').forEach((element) => {
                setMermaidMarkerEnd(element, defaultArrowRef);
            });
        };
        const resolveNodeElement = (target) => {
            let current = target;
            while (current && current !== host) {
                if (current.classList?.contains('node') && current.getAttribute?.('data-node-key')) {
                    return current;
                }
                current = current.parentNode;
            }
            return null;
        };
        const handleMouseMove = (event) => {
            const nodeElement = resolveNodeElement(event.target);
            if (!nodeElement) {
                clearLinkedState();
                return;
            }
            const nodeKey = nodeElement.getAttribute('data-node-key');
            if (!nodeKey) {
                clearLinkedState();
                return;
            }
            clearLinkedState();
            host.querySelectorAll(`[data-source-key="${nodeKey}"], [data-target-key="${nodeKey}"]`).forEach((element) => {
                if (element.classList.contains('blog-mermaid-edge-arrow')) {
                    element.classList.add('blog-mermaid-edge-arrow-linked');
                    return;
                }
                if (element.closest('.edgeLabel, .edgeLabels') || element.classList.contains('label')) {
                    element.classList.add('blog-mermaid-edge-label-linked');
                    return;
                }
                element.classList.add('blog-mermaid-edge-linked');
                setMermaidMarkerEnd(element, activeArrowRef);
            });
        };
        const handleMouseLeave = () => {
            clearLinkedState();
        };
        host.addEventListener('mousemove', handleMouseMove);
        host.addEventListener('mouseleave', handleMouseLeave);
        return () => {
            host.removeEventListener('mousemove', handleMouseMove);
            host.removeEventListener('mouseleave', handleMouseLeave);
            clearLinkedState();
        };
    }, [renderedSvg]);

    return <div className={markdownStyle.mermaidBlock}>
        <div className={markdownStyle.mermaidToolbar}>
            <div
                className={markdownStyle.mermaidCopyBtn}
                onClick={() => onCopy(code)}
                onMouseEnter={onCopyEnter}
                onMouseLeave={onCopyLeave}
            >
                <i className={markdownStyle.iconfont}>&#xe6f3;</i>
            </div>
            <div className={markdownStyle.mermaidLabel}>mermaid</div>
        </div>
        <div className={markdownStyle.mermaidCanvas} ref={canvasRef}>
            {renderedSvg ? <div dangerouslySetInnerHTML={{__html: renderedSvg}}/> : <pre className={markdownStyle.mermaidFallback}>{code}</pre>}
        </div>
        {renderError ? <div className={markdownStyle.mermaidError}>Mermaid render failed: {renderError}</div> : null}
    </div>;
});

export default MermaidBlock;
